import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Paper, Typography } from '@mui/material';
import { ButtonProps } from '@mui/material/Button';
import clsx from 'clsx';
import { useBreakpoints } from 'hooks';
import { isEmpty } from 'lodash';
import { useCheckoutSubscription } from 'services/payments/subscriptions/hooks';
import { useUserStore } from 'store/useUserStore';
import { getAuthUser } from 'store/useUserStore/selectors';
import { useUserSubscriptionStore } from 'store/useUserSubscriptionStore';
import { getUserSubscription } from 'store/useUserSubscriptionStore/selectors';

import { ReactComponent as PlusIcon } from 'assets/icons/PlusIcon.svg';

import { useAuthenticated } from 'hooks/useAuthenticated';

import { AuthModal } from 'interfaces/auth';
import { SUBSCRIPTION_PLAN, SUBSCRIPTION_RECURRENT, SUBSCRIPTION_STATUS } from 'interfaces/subscriptions';

import { Button } from 'components/_basic/Button/Button';
import { useAuthModalsContext } from 'components/AuthModals';
import { CreateProjectModal } from 'components/CreateProjectModal';
import ModalContext from 'components/ModalManager/modal-context';

import { formatDate } from 'helpers/formatDates';
import ToastService from 'helpers/ToastService';

import { FeaturesList } from './components/FeaturesList';
import { TariffCost } from './components/TariffCost';
import { getPlanToDisplay } from './helpers';
import { IPlanCardProps as IProps } from './types';

import styles from './PlanCard.module.scss';

export const PlanCard: React.FC<IProps> = ({ subscriptionData, isYearly, onCancelSubscription }) => {
  const { t, i18n } = useTranslation();

  const { isAuthenticated, isAuthConfiguring } = useAuthenticated();

  const { showModal, hideModal } = useContext(ModalContext);
  const { setAuthModal, setIsModalOpened } = useAuthModalsContext();

  const userSubscription = useUserSubscriptionStore(getUserSubscription);
  const authUser = useUserStore(getAuthUser);
  const { isAboveMobile, isMobile } = useBreakpoints();

  const checkoutMutation = useCheckoutSubscription();

  const isNextSubscriptionFree = userSubscription?.nextSubscription.subscriptionPlan === SUBSCRIPTION_PLAN.FREE;
  const isFree = subscriptionData.tariff === SUBSCRIPTION_PLAN.FREE;
  const isCurrentSubscriptionFree = userSubscription?.currentSubscription.subscriptionPlan === SUBSCRIPTION_PLAN.FREE;
  const isLight = subscriptionData.tariff === SUBSCRIPTION_PLAN.LIGHT;
  const isCurrentSubscriptionActive =
    userSubscription && userSubscription.currentSubscription.status === SUBSCRIPTION_STATUS.ACTIVE;
  const isSubscriptionActive =
    userSubscription &&
    userSubscription.currentSubscription.status === SUBSCRIPTION_STATUS.ACTIVE &&
    userSubscription.currentSubscription.subscriptionPlan === subscriptionData.tariff;
  const isCurrentSubscription = userSubscription?.currentSubscription.subscriptionPlan === subscriptionData.tariff;

  const isCurrentPlan = getPlanToDisplay(
    userSubscription && userSubscription.currentSubscription,
    subscriptionData,
    isYearly,
  );

  const isNextPlan =
    getPlanToDisplay(userSubscription && userSubscription.nextSubscription, subscriptionData, isYearly) &&
    !isCurrentSubscriptionActive &&
    !isNextSubscriptionFree;

  const isSubscriptionHighlighted =
    (isSubscriptionActive && !isFree) ||
    (isCurrentSubscriptionFree && isLight) ||
    (isEmpty(authUser) && isLight) ||
    (!isSubscriptionActive && isCurrentSubscription);

  const buttonProps: ButtonProps = {
    variant: isCurrentPlan ? 'outlined' : 'contained',
    color: isCurrentPlan ? 'info' : 'secondary',
  };

  const getTariffDescription2Text = () => {
    if (
      t(subscriptionData.tariffDescription2) === 'Crea un plan de negocios con IA' ||
      t(subscriptionData.tariffDescription2) === 'Crea un plan de negocio listo para bancos'
    ) {
      return t(subscriptionData.tariffDescription2) + '   \n ';
    }
    else if (isMobile && t(subscriptionData.tariffDescription2) === 'Teilen Sie Ihre Idee und gewinnen Sie Investoren') {
      return t(subscriptionData.tariffDescription2) + '   \n ';
    } else {
      return t(subscriptionData.tariffDescription2);
    }
  };

  const getButtonText = (text: string) => {
    if (
      (text === 'Кредитна картка не потрібна.' || text === 'Ви вже маєте цей план. Почніть зараз!') &&
      isAboveMobile
    ) {
      return text + '   \n ';
    } else {
      return text;
    }
  };

  const handleSelectClick = async () => {
    if (!(isAuthenticated || isAuthConfiguring)) {
      if (isFree) {
        localStorage.setItem('freePlanClicked', 'true');
        localStorage.removeItem('choosedSubscription');
      }

      if (!isFree) {
        const payload = {
          subscriptionPlan: subscriptionData.tariff,
          recurrent: isYearly ? SUBSCRIPTION_RECURRENT.ANNUALLY : SUBSCRIPTION_RECURRENT.MONTHLY,
        };
        localStorage.removeItem('freePlanClicked');
        localStorage.setItem('choosedSubscription', JSON.stringify(payload));
      }
      setAuthModal(AuthModal.SIGN_UP);
      setIsModalOpened(true);
      return;
    }
    if (isCurrentPlan) {
      showModal(CreateProjectModal, {
        close: hideModal,
      });
      return;
    }

    if (isFree) {
      onCancelSubscription();
      return;
    }

    const itemVariant = isYearly ? 'Billed annually' : 'Billed monthly';
    const tariffVariant = subscriptionData.tariff === 'STANDARD' ? 'standard' : 'light';
    const itemId = isYearly ? `${tariffVariant}-yearly` : `${tariffVariant}-monthly`;

    try {
      // Add analytics tracking
      (window as any).dataLayer?.push({
        event: 'begin_checkout',
        ecommerce: {
          value: isYearly
            ? parseInt(subscriptionData.billYearly.tariffCostYearly.slice(1))
            : parseInt(subscriptionData.billMonthly.tariffCostMonthly.slice(1)),
          currency: 'USD',
          items: [
            {
              item_name: subscriptionData.tariff.toLowerCase(),
              item_variant: itemVariant,
              item_id: itemId,
              price: isYearly
                ? parseInt(subscriptionData.billYearly.tariffCostYearly.slice(1))
                : parseInt(subscriptionData.billMonthly.tariffCostMonthly.slice(1)),
            },
          ],
        },
      });

      const payload = {
        subscriptionPlan: subscriptionData.tariff,
        recurrent: isYearly ? SUBSCRIPTION_RECURRENT.ANNUALLY : SUBSCRIPTION_RECURRENT.MONTHLY,
      };

      const response = await checkoutMutation.mutateAsync(payload);

      if (response.url) {
        window.location.href = response.url;
      } else {
        ToastService.showError(t('noNavigationInformation'));
      }
    } catch (error) {
      if (error instanceof Error) {
        ToastService.showError(error.message);
      }
    }
  };

  const getButtonLabel = () => {
    if (isNextPlan && userSubscription) {
      return t('startsOn', { date: formatDate(userSubscription?.currentSubscription.endDate) });
    }
    const isFreeTrialUsed = userSubscription?.hasActiveOrCompletedOrder;

    return (
      <>
        {isCurrentPlan && <PlusIcon className={styles.plusIcon} />}
        {isCurrentPlan
          ? t('createProject')
          : isFree
            ? t('startForFree')
            : isFreeTrialUsed
              ? t('startPlanning')
              : t('start3DaysTrial')}
      </>
    );
  };

  const getButtonId = () => {
    if (isYearly) {
      return `${subscriptionData.tariff.toLowerCase()}-yearly-plan-button`;
    } else {
      return `${subscriptionData.tariff.toLowerCase()}-monthly-plan-button`;
    }
  };

  return (
    <Paper className={clsx(styles.cardStyle, styles.activeCardStyle)} elevation={0}>
      <Box className={styles.headingWrapper}>
        <Typography variant="h4">{t(subscriptionData.tariffTitle)}</Typography>
        {isCurrentPlan && <Box className={styles.currentPlanWrapper}>{t('currentPlan')}</Box>}
      </Box>
      <Box className={styles.tariffDescriptionContainer}>
        <Typography variant="body2" className={styles.tariffDescription}>
          {getTariffDescription2Text()}
        </Typography>
      </Box>
      <TariffCost
        isYearly={isYearly}
        tariffDataMonthly={subscriptionData.billMonthly}
        tariffDataYearly={subscriptionData.billYearly}
        tariffMonthlyCrossed={subscriptionData.billMonthly.tariffCostMonthlyCrossed}
        tariffAnnualMonthlyCrossed={subscriptionData.billYearly.tariffCostMonthlyCrossed}
        isFree={isFree}
      />
      <Box width={'100%'}>
        <Typography variant="subtitle2" className={styles.buttonText}>
          {isCurrentPlan
            ? getButtonText(t('youAlreadyHaveThisPlan'))
            : isFree
              ? getButtonText(t('noCreditCard'))
              : getButtonText(t('noHiddenCharges'))}
        </Typography>
        <Button
          {...buttonProps}
          className={styles.button}
          fullWidth
          isLoading={checkoutMutation.isPending}
          onClick={handleSelectClick}
          disabled={isNextPlan}
          id={getButtonId()}
        >
          <Box className={styles.buttonInfoWrapper}>{getButtonLabel()}</Box>
        </Button>
      </Box>
      <FeaturesList tariffFeatures={subscriptionData.tariffFeatures} />
    </Paper>
  );
};
